import { Link } from 'react-router-dom'
import { CheckSquare, Clock, Gavel, Users } from 'lucide-react'
import { AvatarGroup, Badge } from '@/components/ui'
import { MeetingStatusBadge, PlatformIcon } from '@/components/domain'
import { formatDayLabel, formatDuration, formatTime } from '@/internationalization/format'
import { useT } from '@/internationalization/i18n'
import type { Meeting } from '@/types'

/**
 * One meeting in the list. A single component for both breakpoints, because two
 * (a table row and a card) drift apart in what they show.
 *
 * The layout is a stack that becomes a row at sm: title and status always
 * visible, metadata wraps, and the counts drop off below sm where they do not
 * fit. Whole card is one link — a card with three separate tap targets is a
 * mis-tap generator on a phone.
 */
export function MeetingListItem({
  meeting,
  linkToSeriesWhenRecurring,
  showRecurringBadge = true,
}: {
  meeting: Meeting
  /**
   * When true, a recurring meeting links to its series page instead of its
   * own detail page. Used on the All Meetings list, where clicking a
   * recurring meeting should open the series rather than one occurrence.
   * Occurrences listed inside a series's own Occurrences tab still want
   * the individual meeting, so this defaults to false there. The caller is
   * responsible for passing false when the list is already narrowed by a
   * search or filter -- the series page has no way to highlight or jump to
   * the meeting that was found. A meeting currently recording is never
   * redirected regardless of this prop, since it is unambiguously the one
   * occurrence the person wants.
   */
  linkToSeriesWhenRecurring?: boolean
  /**
   * The "Recurring" badge tells a reader, among a mixed list of meetings,
   * which ones belong to a series -- useful on the All Meetings list, and
   * redundant inside a series's own Occurrences tab, where every row is
   * already known to be part of the same series.
   */
  showRecurringBadge?: boolean
}) {
  const t = useT()
  const isLive = meeting.status === 'recording'
  const to =
    linkToSeriesWhenRecurring && !isLive && meeting.isRecurring && meeting.seriesId
      ? `/meetings/series/${encodeURIComponent(meeting.seriesId)}`
      : `/meetings/${meeting.id}`

  return (
    <li className="border-b border-border last:border-0">
      <Link
        to={to}
        className="flex flex-col gap-2 px-3 py-3 transition-colors hover:bg-surface-muted sm:flex-row sm:items-center sm:gap-4"
      >
        <PlatformIcon platform={meeting.platform} />

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className="truncate text-base font-medium text-fg">{meeting.title}</p>
            {meeting.isShared && (
              <Badge tone="info" size="sm">
                {t('meetings.shared')}
              </Badge>
            )}
            {meeting.isRecurring && showRecurringBadge && (
              <Badge tone="neutral" size="sm">
                Recurring
              </Badge>
            )}
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-fg-muted">
            <span>
              {formatDayLabel(meeting.startsAt)} · {formatTime(meeting.startsAt)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Clock aria-hidden className="h-3.5 w-3.5" />
              {formatDuration(meeting.durationSeconds)}
            </span>
            <span className="inline-flex items-center gap-1">
              <Users aria-hidden className="h-3.5 w-3.5" />
              {meeting.participants.length}
            </span>
            {meeting.decisionCount > 0 && (
              <span className="hidden items-center gap-1 sm:inline-flex">
                <Gavel aria-hidden className="h-3.5 w-3.5" />
                {t('meetings.decisions', { count: meeting.decisionCount })}
              </span>
            )}
            {meeting.actionItemCount > 0 && (
              <span className="hidden items-center gap-1 sm:inline-flex">
                <CheckSquare aria-hidden className="h-3.5 w-3.5" />
                {t('meetings.actions', { count: meeting.actionItemCount })}
              </span>
            )}
          </div>
        </div>

        <div className="flex shrink-0 items-center gap-3">
          <AvatarGroup people={meeting.participants} max={3} className="hidden sm:flex" />
          {isLive ? (
            <Badge tone="danger" dot>{t('meetings.recording')}</Badge>
          ) : (
            <MeetingStatusBadge status={meeting.status} leaveReason={meeting.leaveReason} />
          )}
        </div>
      </Link>
    </li>
  )
}
